import React, { useState, useRef } from 'react';
import { Bold, Italic, Link as LinkIcon, List, ListOrdered, Heading2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const SimpleEditor = ({ value, onChange, placeholder, rows = 16, dataTestId, name = 'content' }) => {
  const textareaRef = useRef(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  const handleTextChange = (e) => {
    // Create a proper event object with name for the parent handler
    onChange({ target: { name: name, value: e.target.value } });
  };

  const insertText = (before, after = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const newText = value.substring(0, start) + before + selectedText + after + value.substring(end);
    
    onChange({ target: { name: name, value: newText } });
    
    // Set cursor position
    setTimeout(() => {
      const newPosition = start + before.length + selectedText.length;
      textarea.focus();
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const handleBold = () => {
    insertText('**', '**');
  };

  const handleItalic = () => {
    insertText('*', '*');
  };

  const handleHeading = () => {
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    const newText = value.substring(0, lineStart) + '## ' + value.substring(lineStart);
    onChange({ target: { name: name, value: newText } });
  };

  const handleBulletList = () => {
    insertText('\n- ', '');
  };

  const handleNumberedList = () => {
    insertText('\n1. ', '');
  };

  const openLinkDialog = () => {
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    
    if (selectedText) {
      setLinkText(selectedText);
    } else {
      setLinkText('');
    }
    setLinkUrl('https://');
    setShowLinkDialog(true);
  };

  const insertLink = () => {
    if (!linkText || !linkUrl) return;
    
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    const linkMarkdown = `[${linkText}](${linkUrl})`;
    const newText = value.substring(0, start) + linkMarkdown + value.substring(end);
    
    onChange({ target: { name: name, value: newText } });
    setShowLinkDialog(false);
    setLinkText('');
    setLinkUrl('https://');
    
    setTimeout(() => {
      textarea.focus();
    }, 0);
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 p-3 bg-gray-50 border border-gray-300 rounded-t-lg border-b-0">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBold}
          className="h-8 w-8 p-0"
          title="Bold (Ctrl+B)"
        >
          <Bold size={16} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleItalic}
          className="h-8 w-8 p-0"
          title="Italic (Ctrl+I)"
        >
          <Italic size={16} />
        </Button>
        <div className="w-px h-8 bg-gray-300"></div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleHeading}
          className="h-8 w-8 p-0"
          title="Heading"
        >
          <Heading2 size={16} />
        </Button>
        <div className="w-px h-8 bg-gray-300"></div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBulletList}
          className="h-8 w-8 p-0"
          title="Bullet List"
        >
          <List size={16} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleNumberedList}
          className="h-8 w-8 p-0"
          title="Numbered List"
        >
          <ListOrdered size={16} />
        </Button>
        <div className="w-px h-8 bg-gray-300"></div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={openLinkDialog}
          className="h-8 px-3"
          title="Insert Link"
        >
          <LinkIcon size={16} className="mr-1" />
          <span className="text-xs">Link</span>
        </Button>
      </div>

      {/* Textarea */}
      <textarea
        ref={textareaRef}
        value={value || ''}
        onChange={handleTextChange}
        placeholder={placeholder}
        rows={rows}
        data-testid={dataTestId}
        name={name}
        className="flex min-h-[80px] w-full rounded-md rounded-t-none border border-input border-t-0 bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        style={{ resize: 'vertical' }}
      />

      {/* Helper Text */}
      <div className="mt-2 text-xs text-gray-500 space-y-1">
        <p><strong>Formatting Tips:</strong></p>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>Double line breaks create new paragraphs</li>
          <li><code>**bold text**</code> for <strong>bold</strong></li>
          <li><code>*italic text*</code> for <em>italic</em></li>
          <li><code>## Heading</code> for headings</li>
          <li>Use the Link button to insert hyperlinks</li>
        </ul>
      </div>

      {/* Link Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Insert Link</DialogTitle>
            <DialogDescription>
              Add a hyperlink to your press release content
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="link-text" className="text-sm font-medium">
                Link Text *
              </Label>
              <Input
                id="link-text"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                placeholder="Click here"
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="link-url" className="text-sm font-medium">
                URL *
              </Label>
              <Input
                id="link-url"
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowLinkDialog(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={insertLink}
              className="bg-navy-600 hover:bg-navy-700 text-white"
              disabled={!linkText || !linkUrl}
            >
              Insert Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SimpleEditor;
